import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ReviewLink } from '../../types/review';

interface PlatformButtonProps {
  platform: ReviewLink;
  onClick: () => void;
  disabled?: boolean;
  config: any;
}

export const PlatformButton: React.FC<PlatformButtonProps> = ({
  platform,
  onClick,
  disabled = false,
  config
}) => {
  const { i18n } = useTranslation();

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-4 py-3 rounded-md text-white font-medium flex items-center justify-center ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
      }`}
      style={{ 
        backgroundColor: disabled ? '#9CA3AF' : config['major-color'],
        borderRadius: config.border_radius
      }}
    >
      {getPlatformIcon(platform.platform, 20)}
      <span className="ml-2">{getPlatformName(platform.platform)}</span>
    </button>
  );
};

// Helper function to get platform icon
export const getPlatformIcon = (platform: string, size: number = 24) => {
  switch (platform) {
    case 'google':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"/>
        </svg>
      );
    case 'tripadvisor':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.6 0 12 0zm0 1.846c5.64 0 10.154 4.514 10.154 10.154 0 5.64-4.514 10.154-10.154 10.154-5.64 0-10.154-4.514-10.154-10.154C1.846 6.36 6.36 1.846 12 1.846zM5.47 7.385c-1.92 0-3.524 1.624-3.524 3.524 0 1.92 1.604 3.524 3.524 3.524 1.92 0 3.524-1.604 3.524-3.524 0-1.9-1.604-3.524-3.524-3.524zm13.06 0c-1.92 0-3.524 1.624-3.524 3.524 0 1.92 1.604 3.524 3.524 3.524 1.92 0 3.524-1.604 3.524-3.524 0-1.9-1.604-3.524-3.524-3.524zM5.47 8.676c1.324 0 2.233.909 2.233 2.233 0 1.324-.909 2.233-2.233 2.233-1.324 0-2.233-.909-2.233-2.233 0-1.324.909-2.233 2.233-2.233zm13.06 0c1.324 0 2.233.909 2.233 2.233 0 1.324-.909 2.233-2.233 2.233-1.324 0-2.233-.909-2.233-2.233 0-1.324.909-2.233 2.233-2.233zM12 9.308c-1.324 0-2.233.909-2.233 2.233 0 1.324.909 2.233 2.233 2.233 1.324 0 2.233-.909 2.233-2.233 0-1.324-.909-2.233-2.233-2.233zm0 .576c.909 0 1.657.748 1.657 1.657 0 .909-.748 1.657-1.657 1.657-.909 0-1.657-.748-1.657-1.657 0-.909.748-1.657 1.657-1.657zM6.5 17C7.3 17 8 16.3 8 15.5C8 14.7 7.3 14 6.5 14C5.7 14 5 14.7 5 15.5C5 16.3 5.7 17 6.5 17ZM17.5 17C18.3 17 19 16.3 19 15.5C19 14.7 18.3 14 17.5 14C16.7 14 16 14.7 16 15.5C16 16.3 16.7 17 17.5 17ZM5 13H19V12H5V13Z"/>
        </svg>
      );
    case 'facebook':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      );
    case 'website':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="2" y1="12" x2="22" y2="12"></line>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
        </svg>
      );
    default:
      return null;
  }
};

// Helper function to get platform name
export const getPlatformName = (platform: string): string => {
  switch (platform) {
    case 'google':
      return 'Google';
    case 'tripadvisor':
      return 'TripAdvisor';
    case 'facebook':
      return 'Facebook';
    case 'website':
      return 'Site Web';
    default:
      return platform;
  }
};

export default PlatformButton;